import { get } from 'svelte/store';
import type { Device } from 'src/types/Device';
import { devices } from 'src/stores/lists/devices';
import { devices_status, devices_settings } from 'src/stores/device-runtime/status';
import { status_configurator } from 'src/helpers/device_data_helpers';
import { real_id } from 'src/helpers/interface_helpers';
import type { DashParameterItem } from '@Shelly/shelly-design-system';

export type DashEnvironmentReading = {
	deviceId: string;
	title: string;
	temperature?: string;
	humidity?: string;
	battery?: string;
	temperatureIcon?: string;
	humidityIcon?: string;
	batteryIcon?: string;
	batteryIconStyle?: string;
	hasTemperature: boolean;
	hasHumidity: boolean;
	hasBattery: boolean;
	online: boolean;
};

const ENVIRONMENT_LABEL_NAMES = new Set(['temperature', 'humidity']);

type DashEnvironmentParameterLabels = {
	temperature: string;
	humidity: string;
	battery: string;
};

export type DashEnvironmentStatusLabels = ReturnType<typeof status_configurator>;

export const getEnvironmentStatusLabels = (
	device: Device,
	status = devices_status.getByRealId(device.id),
	settings = devices_settings.getByRealId(device.id),
): DashEnvironmentStatusLabels => (status ? status_configurator(status, device, settings) : []);

export const labelsHaveEnvironmentReadings = (labels: DashEnvironmentStatusLabels): boolean =>
	labels.some((label) => ENVIRONMENT_LABEL_NAMES.has(String(label?.name)));

export const getDevicesWithEnvironmentReadings = (deviceList: Device[] = Object.values(get(devices))) =>
	deviceList.filter((device) =>
		deviceHasEnvironmentReadings(device, getEnvironmentStatusLabels(device)),
	);

export const deviceHasEnvironmentReadings = (
	device: Device,
	labels = getEnvironmentStatusLabels(device),
): boolean => labelsHaveEnvironmentReadings(labels);

export const getEnvironmentReadingForDevice = (
	device: Device,
	status = devices_status.getByRealId(device.id),
	settings = devices_settings.getByRealId(device.id),
	labels = getEnvironmentStatusLabels(device, status, settings),
): DashEnvironmentReading => {
	const temperatureLabel = labels.find((label) => label.name === 'temperature');
	const humidityLabel = labels.find((label) => label.name === 'humidity');
	const batteryLabel = labels.find((label) => label.name === 'battery');

	return {
		deviceId: String(device.id),
		title: device.name,
		temperature: temperatureLabel?.value,
		humidity: humidityLabel?.value,
		battery: batteryLabel?.value,
		temperatureIcon: temperatureLabel?.icon,
		humidityIcon: humidityLabel?.icon,
		batteryIcon: batteryLabel?.icon,
		batteryIconStyle: batteryLabel?.iconStyle,
		hasTemperature: !!temperatureLabel,
		hasHumidity: !!humidityLabel,
		hasBattery: !!batteryLabel,
		online: !!status?.online,
	};
};

export const getEnvironmentParameters = (
	reading: DashEnvironmentReading,
	labels: DashEnvironmentParameterLabels,
	missingValue: string,
): DashParameterItem[] => {
	const parameters: DashParameterItem[] = [];

	if (reading.hasTemperature) {
		parameters.push({
			id: 'temperature',
			icon: reading.temperatureIcon || 'fa-light fa-temperature-high',
			value: reading.temperature ?? missingValue,
			label: labels.temperature,
			accentColor: '--color-danger',
		});
	}

	if (reading.hasHumidity) {
		parameters.push({
			id: 'humidity',
			icon: reading.humidityIcon || 'fa-light fa-droplet-degree',
			value: reading.humidity ?? missingValue,
			label: labels.humidity,
			accentColor: '--color-primary',
		});
	}

	if (reading.hasBattery) {
		parameters.push({
			id: 'battery',
			icon: reading.batteryIcon || 'fa-duotone fa-light fa-battery-full',
			value: reading.battery ?? missingValue,
			label: labels.battery,
			accentColor: '--color-white',
			iconStyle: reading.batteryIconStyle,
		});
	}

	return parameters;
};

export const getEnvironmentReadingsForCards = (
	cards: { id: string | number; position?: number }[] = [],
	deviceMap: Record<string, Device> = get(devices),
	statusMap: Record<string, unknown> = get(devices_status),
	settingsMap: Record<string, unknown> = get(devices_settings),
): DashEnvironmentReading[] => {
	return [...cards]
		.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
		.map((card) => deviceMap[card.id])
		.filter(Boolean)
		.map((device) => {
			const deviceId = real_id(String(device.id));
			return getEnvironmentReadingForDevice(
				device,
				statusMap[deviceId] as Record<string, unknown> | undefined,
				settingsMap[deviceId] as Record<string, unknown> | undefined,
			);
		});
};
